import React, { useState } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useAuth } from '../contexts/AuthContext';
import { CheckCircle2, AlertCircle, CreditCard, Calendar } from 'lucide-react';

const PaymentForm = ({ onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { currentUser } = useAuth();
  const { updateSubscription, isTrialActive, getDaysLeftInTrial } = useSubscription();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Create payment method
      const { error: paymentMethodError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement(CardElement),
        billing_details: {
          email: currentUser.email,
        },
      });

      if (paymentMethodError) {
        setError(paymentMethodError.message);
        setLoading(false);
        return;
      }

      // Create subscription on the server
      const response = await fetch('https://us-central1-decklink-9fa82.cloudfunctions.net/createSubscription', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          paymentMethodId: paymentMethod.id,
          email: currentUser.email,
          userId: currentUser.uid,
        }),
      });

      const result = await response.json();

      if (result.error) {
        setError(result.error);
      } else {
        // Update subscription in context
        await updateSubscription({
          status: 'active',
          plan: 'basic',
          stripeCustomerId: result.customerId,
          stripeSubscriptionId: result.subscriptionId,
          currentPeriodEnd: result.currentPeriodEnd,
          trialEnd: null
        });
        
        setSuccess(true);
        if (onSuccess) {
          onSuccess();
        }
      }
    } catch (err) {
      setError('An error occurred while processing your payment.');
      console.error('Payment error:', err);
    } finally {
      setLoading(false);
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#ffffff',
        '::placeholder': {
          color: '#aab7c4',
        },
        backgroundColor: 'transparent',
      },
      invalid: {
        color: '#fa755a',
        iconColor: '#fa755a',
      },
    },
  };

  const daysLeft = getDaysLeftInTrial();

  return (
    <div className="max-w-md mx-auto">
      {success ? (
        <div className="text-center">
          <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">Payment Successful!</h3>
          <p className="text-slate-400">Your subscription is now active.</p>
        </div>
      ) : (
        <div className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-2xl p-6">
          <div className="text-center mb-6">
            <CreditCard className="w-12 h-12 text-blue-400 mx-auto mb-4" />
            <h2 className="text-2xl font-light text-white mb-2">Start Your Subscription</h2>
            <p className="text-slate-400">$5/month after your free trial</p>
          </div>

          {isTrialActive() && (
            <div className="bg-blue-900/20 border border-blue-500/30 text-blue-400 px-4 py-3 rounded-lg mb-6 flex items-center">
              <Calendar className="w-5 h-5 mr-2" />
              <span>{daysLeft} days left in your free trial</span>
            </div>
          )}

          {error && (
            <div className="bg-red-900/20 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg mb-6 flex items-center">
              <AlertCircle className="w-5 h-5 mr-2" />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Card Information
              </label>
              <div className="bg-slate-900/50 border border-slate-600 rounded-lg p-3">
                <CardElement options={cardElementOptions} />
              </div>
            </div>

            <div className="bg-slate-900/20 border border-slate-600 rounded-lg p-4">
              <h4 className="text-sm font-medium text-slate-300 mb-2">What you'll get:</h4>
              <ul className="text-sm text-slate-400 space-y-1">
                <li>• Unlimited pitch deck uploads</li>
                <li>• Advanced analytics and insights</li>
                <li>• Custom branding options</li>
                <li>• Priority customer support</li>
                <li>• Early access to new features</li>
              </ul>
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={onCancel}
                className="flex-1 py-3 px-4 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!stripe || loading}
                className="flex-1 py-3 px-4 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                    Processing...
                  </div>
                ) : (
                  'Start Subscription'
                )}
              </button>
            </div>
          </form>

          <p className="text-xs text-slate-500 mt-4 text-center">
            By subscribing, you agree to our Terms of Service and Privacy Policy.
            You can cancel anytime.
          </p>
        </div>
      )}
    </div>
  );
};

export default PaymentForm; 